import { memo, useCallback, useLayoutEffect, useRef, type MouseEvent } from 'react'
import { Button } from '@highlight/ui'
import { trackNotchAction } from '../track-notch'
import { useOpenLiveMeetingInSidepanel } from '../use-open-meeting-in-sidepanel'
import { PARTIAL_SPEAKER_LABELS, useLiveTranscriptPreview } from '@renderer/hooks/audio/use-live-transcript-preview'

/**
 * What's being said right now, filling the width it's given and clipped from the start so the
 * newest words stay visible. Opens the live transcript in the sidepanel.
 */
export const TranscriptPreviewButton = memo(function TranscriptPreviewButton() {
  const preview = useLiveTranscriptPreview()
  const openLiveMeeting = useOpenLiveMeetingInSidepanel()
  const textRef = useRef<HTMLSpanElement>(null)

  // Fades the clipped start only once the line overflows, set before paint so it never lags the text
  useLayoutEffect(() => {
    const text = textRef.current
    if (!text) {
      return
    }
    text.dataset.overflowing = String(text.scrollWidth > text.clientWidth)
  }, [preview?.text])

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      trackNotchAction('open meeting', { trigger: 'transcript preview' })
      openLiveMeeting('transcript')
    },
    [openLiveMeeting],
  )

  // Holds the button's size between utterances so the row doesn't move, without being a target
  if (!preview) {
    return <div aria-hidden className="h-6 w-full" />
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      tone="on-dark"
      block
      aria-label="View transcript"
      className="justify-start gap-1.5 overflow-hidden px-1.5 text-sm"
      onClick={handleClick}
    >
      <span className="shrink-0 text-white/50">{PARTIAL_SPEAKER_LABELS[preview.source]}</span>
      {/* Right-to-left so overflow clips the start; `bdi` keeps the text itself left-to-right */}
      <span
        ref={textRef}
        className="min-w-0 flex-1 overflow-hidden text-left [direction:rtl] data-[overflowing=true]:[mask-image:linear-gradient(to_right,transparent,black_16px)]"
      >
        <bdi>{preview.text}</bdi>
      </span>
    </Button>
  )
})
